import {
  ALERT_THRESHOLDS,
  ALL_PERMISSIONS,
  API_ROUTES,
  CSV_EXPORT_OPTIONS,
  DASHBOARD_SCHEMA_VERSION,
  DASHBOARD_WIDGET_IDS,
  DASHBOARD_WIDGET_PERMISSION_REQUIREMENTS,
  DEFAULT_PRODUCTS_COLUMNS,
  DEFAULT_RESTOCK_COLUMNS,
  DEFAULT_RESTOCK_PARAMS,
  DEFAULT_VELOCITY_WEIGHTS,
  DEFAULT_VISIBLE_COLUMN_IDS,
  ERROR_CODES,
  EXTENDED_VELOCITY_WEIGHTS,
  HEADERS,
  LISTING_FLAG_COLORS,
  MARKETPLACE_DETAILS,
  PAGINATION,
  PERFORMANCE,
  PERMISSIONS,
  PERMISSION_DETAILS,
  PERMISSION_GROUPS,
  PRESET_ROLE_DEFINITIONS,
  PRODUCTS_COLUMN_GROUPS,
  RESTOCK_COLUMN_GROUPS,
  RESTOCK_FILTERS,
  RESTOCK_FILTER_CATEGORIES,
  RESTOCK_STATUS,
  RESTOCK_STATUS_COLORS,
  RESTOCK_STATUS_LABELS,
  RESTOCK_URGENCY_LEVELS,
  ROLE_SLUGS,
  SYNC_INTERVALS,
  TRANSPORTATION_ERROR_CODES,
  VALIDATION,
  ZENO_CAROUSEL_SUGGESTIONS,
  canAccessDashboardWidget,
  getAvailableDashboardWidgets,
  isPermission
} from "./chunk-EX2FHAI7.mjs";
import {
  AlertSeverity,
  AlertType,
  ComposedCostMode,
  FulfillmentChannel,
  INVENTORY_AUDIT_ACTIONS,
  INVENTORY_AUDIT_ACTION_LABELS,
  InventoryLocationType,
  ListingFlag,
  ListingStatus,
  Marketplace,
  PrepType,
  ProductCondition,
  ProductStatus,
  ProductType,
  PurchaseOrderStatus,
  RepriceStatus,
  ShipmentPlanStatus,
  ShipmentStatus,
  ShipmentType,
  SupplierCostBasis,
  SyncStatus
} from "./chunk-PXW7J6QI.mjs";

// src/productImport.ts
var PRODUCT_CSV_ENCODING_HEADER = "__zeroth_csv_encoding";
var PRODUCT_CSV_ENCODING_VERSION = "apostrophe-v1";
function decodeImportableCsvCell(value) {
  return value.startsWith("'") ? value.slice(1) : value;
}
var ProductCsvDecodingError = class extends Error {
  code = "UNSUPPORTED_CSV_ENCODING";
  constructor() {
    super("This CSV uses an unsupported text encoding. Save it as CSV UTF-8 and try again.");
    this.name = "ProductCsvDecodingError";
  }
};
var PRODUCT_IMPORT_TEMPLATE_COLUMNS = [
  { header: "sku", field: "sku" },
  { header: "title", field: "title" },
  { header: "asin", field: "asin" },
  { header: "fnsku", field: "fnsku" },
  { header: "supplier", field: "supplierName" },
  { header: "supplier_cost", field: "supplierCost" },
  { header: "fba_inbound_rate_per_lb", field: "fbaInboundShippingRatePerLb" },
  { header: "fba_prep_cost_override", field: "fbaPrepCostOverride" },
  { header: "fbm_shipping_cost", field: "fbmShippingCost" },
  { header: "fbm_box_cost", field: "fbmBoxCost" },
  { header: "lead_time", field: "leadTimeDays" },
  { header: "prep_time", field: "prepTimeDays" },
  { header: "safety_stock", field: "safetyStockDays" }
];
var HEADER_ALIASES = {
  sku: ["sku", "seller sku", "sellersku", "product sku", "merchant sku"],
  title: ["title", "name", "product name", "product title", "item name", "listing title"],
  asin: ["asin", "amazon asin"],
  fnsku: ["fnsku"],
  upc: ["upc", "upc code"],
  ean: ["ean", "ean code"],
  supplierName: ["supplier", "supplier name", "suppliername", "vendor", "vendor name"],
  supplierCost: [
    "supplier cost",
    "suppliercost",
    "cost",
    "unit cost",
    "cogs",
    "cost per unit",
    "supplier cost per unit"
  ],
  supplierCostBasis: [
    "supplier cost basis",
    "suppliercostbasis",
    "cost basis",
    "rebate basis"
  ],
  supplierSku: ["supplier sku", "suppliersku", "vendor sku", "manufacturer sku"],
  inboundShippingCost: [
    "inbound shipping cost",
    "inbound shipping",
    "inbound cost",
    "inbound shipping cost per unit"
  ],
  prepCost: ["prep cost", "prepcost", "prep cost per unit", "preparation cost"],
  fbaInboundShippingRatePerLb: [
    "fba inbound shipping rate per lb",
    "fba inbound rate per lb",
    "fba freight rate per lb"
  ],
  fbaPrepCostOverride: [
    "fba prep cost override",
    "fba prep override",
    "fba total prep cost per unit"
  ],
  fbmShippingCost: [
    "fbm shipping cost",
    "merchant shipping cost",
    "fbm outbound shipping cost"
  ],
  fbmBoxCost: ["fbm box cost", "merchant box cost", "fbm packaging cost"],
  casePackQty: [
    "case pack qty",
    "case pack quantity",
    "casepackqty",
    "case qty",
    "units per case",
    "case pack"
  ],
  leadTimeDays: ["lead time", "leadtime", "lead time days", "lead time (days)"],
  prepTimeDays: ["prep time", "preptime", "prep time days", "prep time (days)"],
  safetyStockDays: ["safety stock", "safetystock", "safety stock days", "safety days"],
  notes: ["notes", "tags", "comments"],
  weight: ["weight", "package weight", "item weight", "product weight", "weight (lbs)", "weight (kg)"],
  isHazmat: ["hazmat", "is hazmat", "ishazmat", "dangerous goods", "hazardous"],
  status: ["status", "product status", "active/inactive"],
  supplierCostPerCase: ["supplier cost per case", "cost per case", "case cost"],
  alwaysPurchaseInCases: ["always purchase in cases", "purchase in cases", "case purchase"],
  supplierRebate: ["supplier rebate", "rebate"],
  minimumOrderQty: ["minimum order qty", "minimum order quantity", "min order qty", "moq"],
  reshippingCost: ["reshipping cost", "reship cost"],
  repackagingMaterialCost: ["repackaging material cost", "repack material cost"],
  repackagingLaborCost: ["repackaging labor cost", "repack labor cost"],
  locationNumber: ["location", "location number", "warehouse location", "bin", "bin location", "shelf"],
  localQty: ["local qty", "local quantity", "local inventory", "warehouse qty", "warehouse quantity"],
  minPrice: ["min price", "minimum price"],
  maxPrice: ["max price", "maximum price"],
  currentPrice: ["current price", "listing price", "list price"]
};
function normalizeProductImportHeader(value) {
  return value.replace(/^\uFEFF/, "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "");
}
var FIELD_BY_NORMALIZED_HEADER = /* @__PURE__ */ new Map();
for (const [field, aliases] of Object.entries(HEADER_ALIASES)) {
  for (const alias of aliases) {
    FIELD_BY_NORMALIZED_HEADER.set(normalizeProductImportHeader(alias), field);
  }
}
function matchProductImportHeader(value) {
  return FIELD_BY_NORMALIZED_HEADER.get(normalizeProductImportHeader(value)) ?? null;
}
function validateDecodedProductCsv(text) {
  for (let index = 0; index < text.length; index += 1) {
    const code = text.charCodeAt(index);
    const unsupported = code <= 8 || code === 11 || code === 12 || code >= 14 && code <= 31 || code === 127 || code === 129 || code === 141 || code === 143 || code === 144 || code === 157;
    if (unsupported) throw new ProductCsvDecodingError();
  }
}
var WINDOWS_1252_C1 = [
  8364,
  void 0,
  8218,
  402,
  8222,
  8230,
  8224,
  8225,
  710,
  8240,
  352,
  8249,
  338,
  void 0,
  381,
  void 0,
  void 0,
  8216,
  8217,
  8220,
  8221,
  8226,
  8211,
  8212,
  732,
  8482,
  353,
  8250,
  339,
  void 0,
  382,
  376
];
function decodeWindows1252(bytes) {
  const codeUnits = new Uint16Array(bytes.length);
  for (let index = 0; index < bytes.length; index += 1) {
    const byte = bytes[index];
    if (byte >= 128 && byte <= 159) {
      const mapped = WINDOWS_1252_C1[byte - 128];
      if (mapped == null) throw new ProductCsvDecodingError();
      codeUnits[index] = mapped;
    } else {
      codeUnits[index] = byte;
    }
  }
  const chunks = [];
  for (let offset = 0; offset < codeUnits.length; offset += 8192) {
    chunks.push(String.fromCharCode(...codeUnits.subarray(offset, offset + 8192)));
  }
  return chunks.join("");
}
function decodeProductCsvBytes(input) {
  const bytes = input instanceof Uint8Array ? input : new Uint8Array(input);
  let text;
  let encoding;
  try {
    text = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
    encoding = "utf-8";
  } catch {
    try {
      text = decodeWindows1252(bytes);
      encoding = "windows-1252";
    } catch {
      throw new ProductCsvDecodingError();
    }
  }
  validateDecodedProductCsv(text);
  return { text, encoding };
}

// src/prepCategories.ts
var PREP_CATEGORY_OPTIONS = [
  "No prep needed",
  "Adult",
  "Baby products",
  "Fragile/glass",
  "Liquids",
  "Sharp items",
  "Textiles",
  "Plush/Stuffed",
  "Small items",
  "Sets/Kits",
  "Perforated packaging",
  "Powder, pellets and granular",
  "Apparel on hanger",
  "Expiration-dated products",
  "Temperature-sensitive"
];
function isCanonicalPrepCategory(value) {
  return PREP_CATEGORY_OPTIONS.includes(value);
}

// src/prepClassification.ts
var AMAZON_PREP_CATEGORIES = [
  "ADULT",
  "BABY",
  "TEXTILE",
  "HANGER",
  "FRAGILE",
  "LIQUID",
  "SHARP",
  "SMALL",
  "PERFORATED",
  "GRANULAR",
  "SET",
  "NONE"
];
var AMAZON_PREP_TYPES = [
  "ITEM_BLACK_SHRINKWRAP",
  "ITEM_BLANKSTK",
  "ITEM_BOXING",
  "ITEM_BUBBLEWRAP",
  "ITEM_CAP_SEALING",
  "ITEM_DEBUNDLE",
  "ITEM_HANG_GARMENT",
  "ITEM_LABELING",
  "ITEM_NO_PREP",
  "ITEM_POLYBAGGING",
  "ITEM_RMOVHANG",
  "ITEM_SETCREAT",
  "ITEM_SETSTK",
  "ITEM_SIOC",
  "ITEM_SUFFOSTK",
  "ITEM_TAPING"
];
var PREP_PHYSICAL_ACTION_LABELS = {
  labeling: "Labeling",
  polybagging: "Polybagging",
  "bubble-wrap": "Bubble wrap",
  "opaque-bagging": "Opaque bagging",
  "suffocation-warning": "Suffocation stickering",
  "cap-sealing": "Cap sealing",
  "set-marking": "Set stickering",
  boxing: "Boxing",
  taping: "Taping",
  "remove-hanger": "Remove from hanger",
  "hang-garment": "Hang garment",
  debundle: "Debundle",
  "barcode-covering": "Cover individual barcodes",
  "original-packaging": "Ship in original packaging"
};
var US_MARKETPLACE = "ATVPDKIKX0DER";
var MODEL_SOURCE = "https://github.com/amzn/selling-partner-api-models/blob/main/models/fulfillment-inbound-api-model/fulfillmentInbound_2024-03-20.json";
var unverified = {
  status: "unverified",
  source: "Explicit legacy candidate; no accepted setter evidence recorded."
};
var readbackEvidence = {
  status: "observed-readback",
  marketplaceId: US_MARKETPLACE,
  source: "Retained production listPrepDetails response, reviewed 2026-10-05; not proof of setter acceptance."
};
var PREP_CATEGORY_CONTRACTS = {
  ADULT: {
    prepCategory: "ADULT",
    displayName: "Adult",
    physicalActions: ["labeling", "polybagging", "opaque-bagging"],
    prepTypes: ["ITEM_POLYBAGGING", "ITEM_BLACK_SHRINKWRAP"],
    evidence: unverified
  },
  BABY: {
    prepCategory: "BABY",
    displayName: "Baby products",
    physicalActions: ["labeling", "polybagging", "suffocation-warning"],
    prepTypes: ["ITEM_POLYBAGGING", "ITEM_SUFFOSTK"],
    evidence: unverified
  },
  TEXTILE: {
    prepCategory: "TEXTILE",
    displayName: "Textiles",
    physicalActions: ["labeling", "polybagging"],
    prepTypes: ["ITEM_LABELING", "ITEM_POLYBAGGING"],
    evidence: { status: "verified-write", source: `${MODEL_SOURCE} (setPrepDetails static request fixture)` }
  },
  HANGER: {
    prepCategory: "HANGER",
    displayName: "Apparel on hanger",
    physicalActions: ["labeling", "polybagging"],
    prepTypes: ["ITEM_POLYBAGGING"],
    evidence: unverified
  },
  FRAGILE: {
    prepCategory: "FRAGILE",
    displayName: "Fragile/glass",
    physicalActions: ["labeling", "bubble-wrap"],
    prepTypes: ["ITEM_LABELING", "ITEM_BUBBLEWRAP"],
    evidence: readbackEvidence
  },
  LIQUID: {
    prepCategory: "LIQUID",
    displayName: "Liquids",
    physicalActions: ["labeling", "polybagging", "suffocation-warning", "cap-sealing"],
    prepTypes: ["ITEM_LABELING", "ITEM_POLYBAGGING", "ITEM_SUFFOSTK", "ITEM_CAP_SEALING"],
    evidence: readbackEvidence
  },
  SHARP: {
    prepCategory: "SHARP",
    displayName: "Sharp items",
    physicalActions: ["labeling", "bubble-wrap"],
    prepTypes: ["ITEM_BUBBLEWRAP"],
    evidence: unverified
  },
  SMALL: {
    prepCategory: "SMALL",
    displayName: "Small items",
    physicalActions: ["labeling", "polybagging"],
    prepTypes: ["ITEM_LABELING", "ITEM_POLYBAGGING"],
    evidence: readbackEvidence
  },
  PERFORATED: {
    prepCategory: "PERFORATED",
    displayName: "Perforated packaging",
    physicalActions: ["labeling", "polybagging", "suffocation-warning"],
    prepTypes: ["ITEM_POLYBAGGING", "ITEM_SUFFOSTK"],
    evidence: {
      status: "verified-write",
      marketplaceId: US_MARKETPLACE,
      source: "Production setPrepDetails rejection enumerated this exact accepted alternative; reviewed 2026-10-05."
    }
  },
  GRANULAR: {
    prepCategory: "GRANULAR",
    displayName: "Powder, pellets and granular",
    physicalActions: ["labeling", "polybagging", "cap-sealing"],
    prepTypes: ["ITEM_POLYBAGGING", "ITEM_CAP_SEALING"],
    evidence: unverified
  },
  SET: {
    prepCategory: "SET",
    displayName: "Sets/Kits",
    physicalActions: ["labeling", "polybagging", "set-marking"],
    prepTypes: ["ITEM_SETCREAT", "ITEM_POLYBAGGING", "ITEM_LABELING"],
    evidence: {
      status: "verified-write",
      marketplaceId: US_MARKETPLACE,
      source: "2026-10-05 production rejection for 1602366-2PK-EJD explicitly lists this polybagged-set alternative."
    }
  },
  NONE: {
    prepCategory: "NONE",
    displayName: "No prep needed",
    physicalActions: ["labeling"],
    prepTypes: ["ITEM_NO_PREP"],
    evidence: { status: "verified-write", source: `${MODEL_SOURCE} (MskuPrepDetailInput example)` },
    observedReadback: {
      prepTypes: ["ITEM_LABELING", "ITEM_NO_PREP"],
      marketplaceId: US_MARKETPLACE,
      source: "Retained production listPrepDetails response; labeling here does not alter the setter example."
    }
  }
};
var PREP_CATEGORY_ALIASES = {
  "No prep needed": "NONE",
  Adult: "ADULT",
  "Baby products": "BABY",
  "Fragile/glass": "FRAGILE",
  Liquids: "LIQUID",
  "Sharp items": "SHARP",
  Textiles: "TEXTILE",
  "Plush/Stuffed": "TEXTILE",
  "Small items": "SMALL",
  "Sets/Kits": "SET",
  "Perforated packaging": "PERFORATED",
  "Powder, pellets and granular": "GRANULAR",
  "Apparel on hanger": "HANGER",
  "Expiration-dated products": "NONE",
  "Temperature-sensitive": "NONE",
  NO_PREP: "NONE",
  ITEM_NO_PREP: "NONE",
  "Baby products (the item in fully enclosed standard retail packaging)": "NONE",
  "Fragile/glass (hazardous)": "FRAGILE",
  "Apparel on hanger (the bag package have an opening greater than 5 in (12.5 cm))": "HANGER"
};
var PrepCategoryResolutionError = class extends Error {
  constructor(category) {
    super(`Unsupported prep category "${category}". Choose a supported preparation category before submitting to Amazon.`);
    this.category = category;
    this.name = "PrepCategoryResolutionError";
  }
  category;
};
function normalizePrepCategory(value) {
  const category = value.trim();
  if (AMAZON_PREP_CATEGORIES.includes(category)) return category;
  if (Object.prototype.hasOwnProperty.call(PREP_CATEGORY_ALIASES, category)) return PREP_CATEGORY_ALIASES[category];
  throw new PrepCategoryResolutionError(value);
}
function getPrepCategoryContract(value) {
  const contract = PREP_CATEGORY_CONTRACTS[normalizePrepCategory(value)];
  if (value === "Apparel on hanger (the bag package have an opening greater than 5 in (12.5 cm))") {
    return { ...contract, physicalActions: [...contract.physicalActions, "suffocation-warning"] };
  }
  return contract;
}
var API_TYPE_PHYSICAL_ACTIONS = {
  ITEM_BLACK_SHRINKWRAP: ["opaque-bagging"],
  ITEM_BLANKSTK: ["barcode-covering"],
  ITEM_BOXING: ["boxing"],
  ITEM_BUBBLEWRAP: ["bubble-wrap"],
  ITEM_CAP_SEALING: ["cap-sealing"],
  ITEM_DEBUNDLE: ["debundle"],
  ITEM_HANG_GARMENT: ["hang-garment"],
  ITEM_LABELING: ["labeling"],
  ITEM_NO_PREP: [],
  ITEM_POLYBAGGING: ["polybagging"],
  ITEM_RMOVHANG: ["remove-hanger"],
  ITEM_SETCREAT: ["set-marking"],
  ITEM_SETSTK: ["set-marking"],
  ITEM_SIOC: ["original-packaging"],
  ITEM_SUFFOSTK: ["suffocation-warning"],
  ITEM_TAPING: ["taping"]
};
function physicalActionsForApiPrepTypes(prepTypes) {
  const actions = /* @__PURE__ */ new Set();
  for (const prepType of prepTypes) {
    if (!AMAZON_PREP_TYPES.includes(prepType)) {
      throw new Error(`Unsupported Amazon prep type "${prepType}"`);
    }
    for (const action of API_TYPE_PHYSICAL_ACTIONS[prepType]) actions.add(action);
  }
  return [...actions];
}
function normalizePrepPhysicalAction(value) {
  if (Object.prototype.hasOwnProperty.call(PREP_PHYSICAL_ACTION_LABELS, value)) return value;
  const entry = Object.entries(PREP_PHYSICAL_ACTION_LABELS).find(([, label]) => label === value);
  return entry?.[0];
}

// src/research.ts
var RESEARCH_ASSESSMENT_VERSION = "research-policy-v1";
function positiveInteger(value) {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  const integer = Math.trunc(value);
  return integer > 0 ? integer : null;
}
function finiteNumber(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}
function round(value, precision = 2) {
  if (value == null || !Number.isFinite(value)) return null;
  const multiplier = 10 ** precision;
  return Math.round((value + Number.EPSILON) * multiplier) / multiplier;
}
function resolveResearchPackQuantity(input) {
  const itemOverride = positiveInteger(input.itemOverride);
  const fixedDefault = positiveInteger(input.fixedDefault);
  const packageQuantity = positiveInteger(input.packageQuantity);
  const numberOfItems = positiveInteger(input.numberOfItems);
  const providerConflict = packageQuantity != null && numberOfItems != null && packageQuantity !== numberOfItems;
  const detectedUnitsPerPack = providerConflict ? null : packageQuantity;
  if (itemOverride != null) {
    return {
      detectedUnitsPerPack,
      effectiveUnitsPerPack: itemOverride,
      source: "ITEM_OVERRIDE",
      confidence: "HIGH",
      reviewRequired: false,
      evidence: ["Manual item override is sticky and takes precedence over scan defaults and provider metadata."]
    };
  }
  if (input.mode === "FIXED" && fixedDefault != null) {
    return {
      detectedUnitsPerPack,
      effectiveUnitsPerPack: fixedDefault,
      source: "LIST_FIXED",
      confidence: "HIGH",
      reviewRequired: false,
      evidence: ["The fixed scan default supplies the Amazon offer quantity."]
    };
  }
  if (packageQuantity != null && numberOfItems == null) {
    return {
      detectedUnitsPerPack: packageQuantity,
      effectiveUnitsPerPack: packageQuantity,
      source: "PROVIDER_PACKAGE_QUANTITY",
      confidence: "MEDIUM",
      reviewRequired: false,
      evidence: [`Provider package quantity reports ${packageQuantity} units and has no conflicting item count.`]
    };
  }
  if (packageQuantity != null && packageQuantity === numberOfItems) {
    return {
      detectedUnitsPerPack: packageQuantity,
      effectiveUnitsPerPack: packageQuantity,
      source: "PROVIDER_AGREEMENT",
      confidence: "HIGH",
      reviewRequired: false,
      evidence: [`Provider package quantity and number of items agree at ${packageQuantity}.`]
    };
  }
  if (providerConflict) {
    return {
      detectedUnitsPerPack: null,
      effectiveUnitsPerPack: 1,
      source: "PREVIEW_ONE_CONFLICT",
      confidence: "LOW",
      reviewRequired: true,
      evidence: [
        `Provider package quantity (${packageQuantity}) conflicts with number of items (${numberOfItems}).`,
        "One unit is used for preview only until a user confirms the Amazon offer quantity."
      ]
    };
  }
  return {
    detectedUnitsPerPack: null,
    effectiveUnitsPerPack: 1,
    source: "PREVIEW_ONE_MISSING",
    confidence: "LOW",
    reviewRequired: true,
    evidence: [
      numberOfItems != null ? `Provider number of items reports ${numberOfItems}, but package quantity is missing.` : "Provider pack evidence is missing or invalid.",
      "One unit is used for preview only until a user confirms the Amazon offer quantity."
    ]
  };
}
function calculateResearchEconomics(input) {
  const supplierCostPerEach = finiteNumber(input.supplierCostPerEach);
  const sellingPrice = finiteNumber(input.sellingPrice);
  const effectiveUnitsPerPack = positiveInteger(input.effectiveUnitsPerPack) ?? 1;
  const shippingCost = finiteNumber(input.shippingCost);
  const prepCost = finiteNumber(input.prepCost);
  const vatRatePercent = finiteNumber(input.vatRatePercent);
  const fbaFee = finiteNumber(input.fbaFee);
  const referralFee = finiteNumber(input.referralFee);
  const storageFee = finiteNumber(input.storageFee);
  const estimatedMonthlyUnitsSold = finiteNumber(input.estimatedMonthlyUnitsSold);
  const missingInputs = [];
  if (supplierCostPerEach == null) missingInputs.push("supplierCostPerEach");
  if (sellingPrice == null) missingInputs.push("sellingPrice");
  if (positiveInteger(input.effectiveUnitsPerPack) == null) missingInputs.push("effectiveUnitsPerPack");
  if (shippingCost == null) missingInputs.push("shippingCost");
  if (prepCost == null) missingInputs.push("prepCost");
  if (vatRatePercent == null) missingInputs.push("vatRatePercent");
  if (fbaFee == null) missingInputs.push("fbaFee");
  if (referralFee == null) missingInputs.push("referralFee");
  if (storageFee == null) missingInputs.push("storageFee");
  const assumptions = [];
  if (fbaFee == null || referralFee == null || storageFee == null) {
    assumptions.push("Missing Amazon fee components are shown as $0 in this estimate.");
  }
  if (shippingCost == null || prepCost == null) {
    assumptions.push("Missing shipping or prep costs are shown as $0 in this estimate.");
  }
  if (vatRatePercent == null) {
    assumptions.push("Missing VAT is shown as 0% in this estimate.");
  }
  const amazonFeesTotal = fbaFee == null && referralFee == null && storageFee == null ? null : round((fbaFee ?? 0) + (referralFee ?? 0) + (storageFee ?? 0));
  const landedCost = supplierCostPerEach == null ? null : round(
    supplierCostPerEach * effectiveUnitsPerPack + (shippingCost ?? 0) + (prepCost ?? 0)
  );
  const vatCost = sellingPrice == null ? null : round(sellingPrice * ((vatRatePercent ?? 0) / 100));
  const sellerProceeds = sellingPrice == null ? null : round(sellingPrice - (amazonFeesTotal ?? 0));
  const estimatedMonthlyRevenue = sellingPrice != null && estimatedMonthlyUnitsSold != null ? round(sellingPrice * estimatedMonthlyUnitsSold) : null;
  const netProfit = sellingPrice != null && landedCost != null && vatCost != null ? round(sellingPrice - (amazonFeesTotal ?? 0) - landedCost - vatCost) : null;
  const netMargin = netProfit != null && sellingPrice != null && sellingPrice > 0 ? round(netProfit / sellingPrice * 100) : null;
  const roi = netProfit != null && landedCost != null && landedCost > 0 ? round(netProfit / landedCost * 100) : null;
  return {
    supplierCostPerEach,
    sellingPrice,
    effectiveUnitsPerPack,
    shippingCost: shippingCost ?? 0,
    prepCost: prepCost ?? 0,
    vatRatePercent: vatRatePercent ?? 0,
    landedCost,
    vatCost,
    amazonFeesTotal,
    sellerProceeds,
    estimatedMonthlyRevenue,
    netProfit,
    netMargin,
    roi,
    perEachProfit: netProfit == null ? null : round(netProfit / effectiveUnitsPerPack),
    complete: missingInputs.length === 0,
    missingInputs,
    assumptions
  };
}
function factor(key, label, status, detail) {
  return { key, label, status, detail };
}
function buildResearchAssessment(input) {
  const { economics, packResolution } = input;
  const factors = [];
  const restrictionBlocked = input.restrictionStatus === "RESTRICTED";
  const restrictionNeedsReview = input.restrictionStatus === "APPROVAL_REQUIRED" || input.restrictionStatus === "UNKNOWN" || input.restrictionStatus === "ERROR";
  factors.push(factor(
    "restriction",
    "Selling eligibility",
    restrictionBlocked ? "BLOCKED" : restrictionNeedsReview ? "REVIEW" : "PASS",
    restrictionBlocked ? "Amazon reports that this offer is restricted." : restrictionNeedsReview ? "Selling eligibility needs confirmation." : "Amazon selling eligibility is allowed."
  ));
  factors.push(factor(
    "pack",
    "Pack evidence",
    packResolution.reviewRequired ? "REVIEW" : "PASS",
    packResolution.reviewRequired ? "Confirm the Amazon offer quantity before acting." : `${packResolution.effectiveUnitsPerPack} unit${packResolution.effectiveUnitsPerPack === 1 ? "" : "s"} per Amazon offer from ${packResolution.source.toLowerCase().replaceAll("_", " ")}.`
  ));
  factors.push(factor(
    "economics",
    "Economics inputs",
    economics.complete ? "PASS" : "REVIEW",
    economics.complete ? "Cost, price, fees, shipping, prep, and VAT inputs are complete." : `Complete ${economics.missingInputs.join(", ")} before acting.`
  ));
  factors.push(factor(
    "freshness",
    "Input freshness",
    input.inputsStale ? "REVIEW" : "PASS",
    input.inputsStale ? "Provider inputs are stale and should be refreshed." : "Provider inputs are current."
  ));
  const roiPass = economics.roi != null && economics.roi > 30;
  const profitPass = economics.netProfit != null && economics.netProfit > 3;
  const sellersPass = input.numberOfSellers != null && input.numberOfSellers < 15;
  factors.push(factor(
    "roi",
    "ROI over 30%",
    roiPass ? "PASS" : "FAIL",
    economics.roi == null ? "ROI is unavailable." : `ROI is ${economics.roi.toFixed(2)}%.`
  ));
  factors.push(factor(
    "profit",
    "Profit over $3",
    profitPass ? "PASS" : "FAIL",
    economics.netProfit == null ? "Net profit is unavailable." : `Net profit is $${economics.netProfit.toFixed(2)}.`
  ));
  factors.push(factor(
    "sellers",
    "Fewer than 15 sellers",
    sellersPass ? "PASS" : "FAIL",
    input.numberOfSellers == null ? "Seller count is unavailable." : `${input.numberOfSellers} seller${input.numberOfSellers === 1 ? "" : "s"} reported.`
  ));
  const reviewRequired = restrictionNeedsReview || packResolution.reviewRequired || !economics.complete || input.inputsStale;
  const action = restrictionBlocked ? "BLOCKED" : reviewRequired ? "REVIEW" : roiPass && profitPass && sellersPass ? "SHORTLIST" : "LOW_FIT";
  const reasons = factors.filter((entry) => action === "SHORTLIST" ? entry.status === "PASS" : entry.status !== "PASS").map((entry) => entry.detail);
  return {
    action,
    confidence: restrictionBlocked || !reviewRequired && economics.complete ? "HIGH" : packResolution.confidence === "MEDIUM" && !input.inputsStale ? "MEDIUM" : "LOW",
    reasons,
    factors,
    assumptions: [...packResolution.evidence, ...economics.assumptions],
    version: RESEARCH_ASSESSMENT_VERSION,
    evaluatedAt: input.evaluatedAt
  };
}

// src/supplierOrderAmounts.ts
var SUPPLIER_ORDER_AMOUNT_FIELDS = ["minimumOrderValue", "freeFreightMinimum", "targetPOValue"];
var SUPPLIER_ORDER_AMOUNT_LABELS = {
  minimumOrderValue: "Minimum Order Value",
  freeFreightMinimum: "Free Freight Min",
  targetPOValue: "Target PO Value"
};
var SUPPLIER_ORDER_AMOUNT_MAX = 9999999999e-2;
var SUPPLIER_ORDER_AMOUNT_ERROR = "Enter an amount from 0 to 99,999,999.99 with at most two decimal places.";
function isValidSupplierOrderAmount(value) {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 && value <= SUPPLIER_ORDER_AMOUNT_MAX && /^\d+(?:\.\d{1,2})?$/.test(String(value));
}

// src/guardrailRoiDisplay.ts
var finite = (value) => typeof value === "number" && Number.isFinite(value);
function guardrailRoiDisplay(row, bound) {
  const stored = row.guardrails[bound === "min" ? "minRoiPercent" : "maxRoiPercent"];
  const derived = row.guardrails[bound === "min" ? "minDerivedRoiPercent" : "maxDerivedRoiPercent"];
  const contexts = row.economics.priceFees;
  for (const value2 of [stored, derived, contexts[bound].roiPercent]) {
    if (finite(value2)) return { value: value2, approximate: false, sourcePrice: null, reason: null };
  }
  const unavailable = (reason) => ({ value: null, approximate: false, sourcePrice: null, reason });
  const price = row.prices[bound === "min" ? "minPrice" : "maxPrice"];
  const cost = row.economics.productCostBasis;
  const local = row.economics.localFeeLikeExpensesPerUnit;
  if (!finite(price) || price <= 0) return unavailable("Needs bound price");
  if (!finite(cost) || cost <= 0) return unavailable("Needs product cost");
  if (!finite(local) || local < 0) return unavailable("Needs local expenses");
  const usable = (ctx) => finite(ctx.priceBasis) && ctx.priceBasis > 0 && finite(ctx.amazonFeesPerUnit) && ctx.amazonFeesPerUnit >= 0 && finite(ctx.referralFeePerUnit) && ctx.referralFeePerUnit >= 0 && ctx.referralFeePerUnit <= ctx.amazonFeesPerUnit;
  const source = usable(contexts.listed) ? contexts.listed : [contexts.suggested, contexts.min, contexts.max].filter(usable).sort((a, b) => Math.abs(a.priceBasis - price) - Math.abs(b.priceBasis - price))[0];
  if (!source) return unavailable("Needs fee estimate");
  const fees = source.amazonFeesPerUnit - source.referralFeePerUnit + source.referralFeePerUnit * price / source.priceBasis;
  const value = (price - cost - local - fees) / cost * 100;
  if (!Number.isFinite(value)) return unavailable("Needs fee estimate");
  return { value, approximate: true, sourcePrice: source.priceBasis, reason: null };
}

// src/repricingColumnSort.ts
var REPRICING_COLUMN_SORTS = {
  "sellerSnapRoi": {
    "type": "number",
    "paths": [
      "supplemental.sellerSnapRoi"
    ]
  },
  "sellerSnapMinMarkup": {
    "type": "number",
    "paths": [
      "supplemental.sellerSnapMinMarkup"
    ]
  },
  "sellerSnapMaxMarkup": {
    "type": "number",
    "paths": [
      "supplemental.sellerSnapMaxMarkup"
    ]
  },
  "additionalCost": {
    "type": "number",
    "paths": [
      "supplemental.additionalCost"
    ]
  },
  "avgWinPrice24h": {
    "type": "number",
    "paths": [
      "supplemental.avgWinPrice24h",
      "supplemental.avgWinPrice"
    ]
  },
  "avgBusinessBuyBoxShare": {
    "type": "number",
    "paths": [
      "business.avgBusinessBuyBoxShare"
    ]
  },
  "avgSalesRank": {
    "type": "number",
    "paths": [
      "supplemental.avgSalesRank"
    ]
  },
  "b2bOrderedItems": {
    "type": "number",
    "paths": [
      "business.orderedItems"
    ]
  },
  "b2bOrderedItemsDiff": {
    "type": "number",
    "paths": [
      "business.orderedItemsDiff"
    ]
  },
  "b2bOrderedItemsPrevious": {
    "type": "number",
    "paths": [
      "business.orderedItemsPrevious"
    ]
  },
  "b2bProfitMargin": {
    "type": "number",
    "paths": [
      "business.profitMargin"
    ]
  },
  "b2bRoi": {
    "type": "number",
    "paths": [
      "business.roi"
    ]
  },
  "b2bTotalProfit": {
    "type": "number",
    "paths": [
      "business.totalProfit"
    ]
  },
  "b2bTotalProfitDiff": {
    "type": "number",
    "paths": [
      "business.totalProfitDiff"
    ]
  },
  "b2bTotalProfitPrevious": {
    "type": "number",
    "paths": [
      "business.totalProfitPrevious"
    ]
  },
  "b2bTotalRevenue": {
    "type": "number",
    "paths": [
      "business.totalRevenue"
    ]
  },
  "b2bTotalRevenueDiff": {
    "type": "number",
    "paths": [
      "business.totalRevenueDiff"
    ]
  },
  "b2bTotalRevenuePrevious": {
    "type": "number",
    "paths": [
      "business.totalRevenuePrevious"
    ]
  },
  "brand": {
    "type": "text",
    "paths": [
      "catalog.brand"
    ]
  },
  "businessPricing": {
    "type": "text",
    "paths": [
      "business.pricing"
    ]
  },
  "compPriceThreshold": {
    "type": "number",
    "paths": [
      "integrations.compPriceThreshold"
    ]
  },
  "considerCpt": {
    "type": "text",
    "paths": [
      "integrations.considerCpt"
    ]
  },
  "currentSalesRank": {
    "type": "number",
    "paths": [
      "catalog.salesRank"
    ]
  },
  "currentB2bBuyBoxPrice": {
    "type": "number",
    "paths": [
      "business.currentB2bBuyBoxPrice"
    ]
  },
  "dateCreated": {
    "type": "date",
    "paths": [
      "catalog.createdAt"
    ]
  },
  "daysCover": {
    "type": "number",
    "paths": [
      "inventory.daysCover"
    ]
  },
  "estOutOfStockDate": {
    "type": "date",
    "paths": [
      "supplemental.estOutOfStockDate"
    ]
  },
  "estShortTermStorageFee": {
    "type": "number",
    "paths": [
      "supplemental.estShortTermStorageFee"
    ]
  },
  "fbaAvailableQty": {
    "type": "number",
    "paths": [
      "inventory.fulfillable"
    ]
  },
  "fulfillableQuantity": {
    "type": "number",
    "paths": [
      "inventory.fulfillable"
    ]
  },
  "fbaFeeTiers": {
    "type": "text",
    "paths": [
      "supplemental.fbaFeeTiers"
    ]
  },
  "fbaInboundQty": {
    "type": "number",
    "paths": [
      "inventory.inbound"
    ]
  },
  "fbaResearchingQty": {
    "type": "number",
    "paths": [
      "supplemental.fbaResearchingQty"
    ]
  },
  "fbaReservedQty": {
    "type": "number",
    "paths": [
      "inventory.reserved"
    ]
  },
  "fbaTotalQty": {
    "type": "number",
    "paths": [
      "inventory.fulfillable",
      "inventory.inbound",
      "inventory.reserved",
      "inventory.unfulfillable"
    ],
    "sum": true
  },
  "fbaUnsellableQty": {
    "type": "number",
    "paths": [
      "inventory.unfulfillable"
    ]
  },
  "fbaWarehouseQty": {
    "type": "number",
    "paths": [
      "inventory.fulfillable",
      "inventory.reserved"
    ],
    "sum": true
  },
  "fnsku": {
    "type": "text",
    "paths": [
      "fnsku"
    ]
  },
  "fulfillmentCost": {
    "type": "number",
    "paths": [
      "integrations.fulfillmentCost"
    ]
  },
  "historicalDaysOfSupply": {
    "type": "number",
    "paths": [
      "supplemental.historicalDaysOfSupply"
    ]
  },
  "inventoryAge0To30": {
    "type": "number",
    "paths": [
      "inventory.age0To30"
    ]
  },
  "inventoryAge0To90": {
    "type": "number",
    "paths": [
      "inventory.age0To90"
    ]
  },
  "inventoryAge181To270": {
    "type": "number",
    "paths": [
      "inventory.age181To270"
    ]
  },
  "inventoryAge271To365": {
    "type": "number",
    "paths": [
      "inventory.age271To365"
    ]
  },
  "inventoryAge31To60": {
    "type": "number",
    "paths": [
      "inventory.age31To60"
    ]
  },
  "inventoryAge365Plus": {
    "type": "number",
    "paths": [
      "inventory.age365Plus"
    ]
  },
  "inventoryAge61To90": {
    "type": "number",
    "paths": [
      "inventory.age61To90"
    ]
  },
  "inventoryAge91To180": {
    "type": "number",
    "paths": [
      "inventory.age91To180"
    ]
  },
  "inventoryValue": {
    "type": "number",
    "paths": [
      "inventory.inventoryValue"
    ]
  },
  "lastPurchaseDate": {
    "type": "date",
    "paths": [
      "catalog.lastPurchaseDate"
    ]
  },
  "lastPurchasePrice": {
    "type": "number",
    "paths": [
      "catalog.lastPurchasePrice"
    ]
  },
  "leadTimeDays": {
    "type": "number",
    "paths": [
      "inventory.leadTimeDays"
    ]
  },
  "lowPriceFba": {
    "type": "number",
    "paths": [
      "integrations.lowPriceFba"
    ]
  },
  "manufacturer": {
    "type": "text",
    "paths": [
      "catalog.manufacturer"
    ]
  },
  "markupAmount": {
    "type": "number",
    "paths": [
      "integrations.markupAmount"
    ]
  },
  "markupOnCost": {
    "type": "number",
    "paths": [
      "integrations.markupOnCost"
    ]
  },
  "mpn": {
    "type": "text",
    "paths": [
      "catalog.mpn"
    ]
  },
  "note": {
    "type": "text",
    "paths": [
      "catalog.note"
    ]
  },
  "orderedItems": {
    "type": "number",
    "paths": [
      "sales.orderedItems30d"
    ]
  },
  "packageHeight": {
    "type": "number",
    "paths": [
      "catalog.packageHeight"
    ]
  },
  "packageLength": {
    "type": "number",
    "paths": [
      "catalog.packageLength"
    ]
  },
  "packageVolume": {
    "type": "number",
    "paths": [
      "catalog.packageVolume"
    ]
  },
  "packageWeight": {
    "type": "number",
    "paths": [
      "catalog.packageWeight"
    ]
  },
  "packageWidth": {
    "type": "number",
    "paths": [
      "catalog.packageWidth"
    ]
  },
  "profitAfterReturns30d": {
    "type": "number",
    "paths": [
      "financial.profitAfterReturns30d"
    ]
  },
  "promoReducedProfitRange": {
    "type": "text",
    "paths": [
      "supplemental.promoReducedProfitRange",
      "financial.promoReducedProfitRange"
    ]
  },
  "referralFeePromotion": {
    "type": "number",
    "paths": [
      "financial.referralFeePromotion"
    ]
  },
  "referencePrice": {
    "type": "number",
    "paths": [
      "integrations.referencePrice"
    ]
  },
  "reimbursedCash": {
    "type": "number",
    "paths": [
      "financial.reimbursedCash"
    ]
  },
  "reimbursedInventory": {
    "type": "number",
    "paths": [
      "financial.reimbursedInventory"
    ]
  },
  "replenishBy": {
    "type": "date",
    "paths": [
      "inventory.replenishBy"
    ]
  },
  "reqDaysCover": {
    "type": "number",
    "paths": [
      "inventory.reqDaysCover"
    ]
  },
  "returnRate": {
    "type": "number",
    "paths": [
      "financial.returnRate"
    ]
  },
  "returnedItems": {
    "type": "number",
    "paths": [
      "financial.returnedItems30d"
    ]
  },
  "returnsCosts": {
    "type": "number",
    "paths": [
      "financial.returnsCost30d"
    ]
  },
  "revenueAfterReturns": {
    "type": "number",
    "paths": [
      "financial.revenueAfterReturns30d"
    ]
  },
  "reservedCustomerOrders": {
    "type": "number",
    "paths": [
      "inventory.reservedCustomerOrder"
    ]
  },
  "reservedFcProcessing": {
    "type": "number",
    "paths": [
      "inventory.reservedFcProcessing"
    ]
  },
  "reservedFcTransfers": {
    "type": "number",
    "paths": [
      "inventory.reservedFcTransfer"
    ]
  },
  "sellThrough": {
    "type": "number",
    "paths": [
      "inventory.sellThrough"
    ]
  },
  "shipPrice": {
    "type": "number",
    "paths": [
      "integrations.shipPrice"
    ]
  },
  "storeName": {
    "type": "text",
    "paths": [
      "store.name"
    ]
  },
  "suggestedQtyReplenish": {
    "type": "number",
    "paths": [
      "inventory.suggestedQtyReplenish"
    ]
  },
  "totalProfit": {
    "type": "number",
    "paths": [
      "economics.totalProfit30d"
    ]
  },
  "totalProfitDiff": {
    "type": "number",
    "paths": [
      "financial.totalProfitDiff30d"
    ]
  },
  "totalProfitPrevious": {
    "type": "number",
    "paths": [
      "financial.totalProfitPrevious30d"
    ]
  },
  "totalRevenue": {
    "type": "number",
    "paths": [
      "economics.totalRevenue30d"
    ]
  },
  "totalRevenueDiff": {
    "type": "number",
    "paths": [
      "financial.totalRevenueDiff30d"
    ]
  },
  "totalRevenuePrevious": {
    "type": "number",
    "paths": [
      "financial.totalRevenuePrevious30d"
    ]
  },
  "velocityLast30d": {
    "type": "number",
    "paths": [
      "sales.velocity30d"
    ]
  },
  "velocityLast7d": {
    "type": "number",
    "paths": [
      "sales.velocity7d"
    ]
  },
  "walmartPriceSync": {
    "type": "text",
    "paths": [
      "integrations.walmartPriceSync"
    ]
  },
  "walmartSku": {
    "type": "text",
    "paths": [
      "integrations.walmartSku"
    ]
  },
  "sellerSnapMinMarkupAmount": {
    "type": "number",
    "paths": [
      "supplemental.sellerSnapMinMarkupAmount"
    ]
  },
  "sellerSnapMaxMarkupAmount": {
    "type": "number",
    "paths": [
      "supplemental.sellerSnapMaxMarkupAmount"
    ]
  },
  "title": {
    "type": "text",
    "paths": [
      "title"
    ]
  },
  "sku": {
    "type": "text",
    "paths": [
      "sku"
    ]
  },
  "asin": {
    "type": "text",
    "paths": [
      "asin"
    ]
  },
  "repricing": {
    "type": "text",
    "paths": [
      "repriceStatus"
    ]
  },
  "profile": {
    "type": "text",
    "paths": [
      "profile.name"
    ]
  },
  "fulfillment": {
    "type": "text",
    "paths": [
      "fulfillmentChannel"
    ]
  },
  "supplier": {
    "type": "text",
    "paths": [
      "supplier.name"
    ]
  },
  "action": {
    "type": "text",
    "paths": [
      "priceSuggestion.action"
    ]
  },
  "confidence": {
    "type": "text",
    "paths": [
      "priceSuggestion.confidence"
    ]
  },
  "policy": {
    "type": "text",
    "paths": [
      "profile.strategy"
    ]
  },
  "quantity": {
    "type": "number",
    "paths": [
      "inventory.fulfillable"
    ]
  },
  "inbound": {
    "type": "number",
    "paths": [
      "inventory.inbound"
    ]
  },
  "local": {
    "type": "number",
    "paths": [
      "inventory.local"
    ]
  },
  "onOrder": {
    "type": "number",
    "paths": [
      "inventory.onOrder"
    ]
  },
  "inventory": {
    "type": "number",
    "paths": [
      "inventory.ageDays"
    ]
  },
  "cost": {
    "type": "number",
    "paths": [
      "economics.productCostBasis",
      "economics.supplierCost"
    ]
  },
  "fees": {
    "type": "number",
    "paths": [
      "economics.priceFees.suggested.amazonFeesPerUnit"
    ]
  },
  "minPrice": {
    "type": "number",
    "paths": [
      "prices.minPrice"
    ]
  },
  "maxPrice": {
    "type": "number",
    "paths": [
      "prices.maxPrice"
    ]
  },
  "buyBox": {
    "type": "number",
    "paths": [
      "prices.buyBoxPrice"
    ]
  },
  "listed": {
    "type": "number",
    "paths": [
      "liveRepricer.currentPrice"
    ]
  },
  "suggested": {
    "type": "number",
    "paths": [
      "prices.suggestedPrice"
    ]
  },
  "orders": {
    "type": "number",
    "paths": [
      "sales.orderedItems30d"
    ]
  },
  "buyBoxShare": {
    "type": "number",
    "paths": [
      "market.buyBoxShare24h"
    ]
  },
  "velocity": {
    "type": "number",
    "paths": [
      "sales.velocity30d"
    ]
  },
  "competitors": {
    "type": "number",
    "paths": [
      "market.competitorCount",
      "market.latestSnapshot.offerCount"
    ]
  },
  "baseCost": {
    "type": "number",
    "paths": [
      "economics.productCostBasis",
      "economics.localFeeLikeExpensesPerUnit"
    ],
    "sum": true
  },
  "roi": {
    "type": "number",
    "paths": [
      "economics.suggestedRoiPercent"
    ]
  },
  "profit": {
    "type": "number",
    "paths": [
      "economics.suggestedProfit"
    ]
  },
  "minRoi": {
    "type": "number",
    "paths": [
      "guardrails.minRoiPercent",
      "guardrails.minDerivedRoiPercent",
      "economics.priceFees.min.roiPercent"
    ]
  },
  "maxRoi": {
    "type": "number",
    "paths": [
      "guardrails.maxRoiPercent",
      "guardrails.maxDerivedRoiPercent",
      "economics.priceFees.max.roiPercent"
    ]
  },
  "margin": {
    "type": "number",
    "paths": [
      "economics.suggestedMarginPercent"
    ]
  },
  "revenue": {
    "type": "number",
    "paths": [
      "economics.totalRevenue30d"
    ]
  },
  "unitProfit": {
    "type": "number",
    "paths": [
      "economics.suggestedProfit"
    ]
  },
  "orders7d": {
    "type": "number",
    "paths": [
      "sales.orderedItems7d"
    ]
  },
  "buyBoxAverage": {
    "type": "number",
    "paths": [
      "market.buyBoxShareAvg"
    ]
  },
  "lowestOffer": {
    "type": "number",
    "paths": [
      "market.latestSnapshot.lowestFbaPrice",
      "market.latestSnapshot.lowestFbmPrice"
    ]
  },
  "referralFee": {
    "type": "number",
    "paths": [
      "economics.priceFees.suggested.referralFeePerUnit"
    ]
  },
  "otherAmazonFee": { "type": "number", "paths": ["economics.priceFees.suggested.otherAmazonFeesPerUnit"] },
  "fulfillmentFee": {
    "type": "number",
    "paths": [
      "economics.priceFees.suggested.fbaFulfillmentFeePerUnit"
    ]
  }
};
function repricingColumnSortValue(row, field) {
  if (field === "baseCost") {
    const economics = row.economics;
    const cost = economics.productCostBasis ?? economics.supplierCost;
    return cost == null ? null : Math.round((cost + (economics.localFeeLikeExpensesPerUnit ?? 0)) * 100) / 100;
  }
  if (field === "minRoi" || field === "maxRoi") return guardrailRoiDisplay(row, field === "minRoi" ? "min" : "max").value;
  const spec = REPRICING_COLUMN_SORTS[field];
  const values = spec.paths.map((path) => path.split(".").reduce((value, key) => value != null && typeof value === "object" ? value[key] : void 0, row));
  if (spec.sum) return values.every((v) => v != null && Number.isFinite(Number(v))) ? values.reduce((sum, v) => sum + Number(v), 0) : null;
  return values.find((value) => value != null && value !== "") ?? null;
}

// src/inventoryAuditSorting.ts
var INVENTORY_AUDIT_SORT_FIELDS = ["date", "product", "sku", "action", "reason", "reference", "actor", "adjustment", "onHandDelta", "reservedDelta", "inboundDelta", "onOrderDelta", "balance", "note"];
function inventoryAuditSortValue(row, field) {
  switch (field) {
    case "date":
      return row.occurredAt;
    case "product":
      return row.product?.title ?? null;
    case "sku":
      return row.product?.sku ?? null;
    case "action":
      return INVENTORY_AUDIT_ACTION_LABELS[row.action];
    case "reason":
      return row.reason;
    case "reference":
      return row.reference?.label ?? null;
    case "actor":
      return row.actor.label;
    case "note":
      return row.note;
    case "adjustment":
      return row.stockChanges[0]?.delta ?? null;
    case "balance":
      return row.stockChanges.find((v) => v.bucket === "AVAILABLE")?.balanceAfter ?? null;
    default: {
      const bucket = { onHandDelta: "ON_HAND", reservedDelta: "RESERVED", inboundDelta: "INBOUND", onOrderDelta: "ON_ORDER" }[field];
      return row.stockChanges.find((v) => v.bucket === bucket)?.delta ?? null;
    }
  }
}
function inventoryAuditSortType(field) {
  return field === "date" ? "date" : ["adjustment", "onHandDelta", "reservedDelta", "inboundDelta", "onOrderDelta", "balance"].includes(field) ? "number" : "text";
}

// src/supplierRebatePreview.ts
function percentageRebate(cost, percent) {
  const decimal = (value) => {
    const [mantissa, exponent = "0"] = String(value).toLowerCase().split("e");
    const [whole, fraction = ""] = mantissa.split(".");
    return [BigInt(whole + fraction), fraction.length - Number(exponent)];
  };
  const [costInteger, costScale] = decimal(cost);
  const [percentInteger, percentScale] = decimal(percent);
  const scale = costScale + percentScale + 2 - 4;
  const numerator = costInteger * percentInteger;
  if (scale <= 0) return Number(numerator * 10n ** BigInt(-scale)) / 1e4;
  const denominator = 10n ** BigInt(scale);
  return Number((numerator * 2n + denominator) / (denominator * 2n)) / 1e4;
}
function effectiveProductRebate(input) {
  const percentOfCost = (percent) => {
    if (percent == null || !(percent > 0 && percent < 100)) return 0;
    if (input.supplierCost == null || !Number.isFinite(input.supplierCost) || input.supplierCost < 0) return null;
    return percentageRebate(input.supplierCost, percent);
  };
  switch (input.mode) {
    case "AMOUNT":
      return input.amount != null && input.amount >= 0 ? input.amount : 0;
    case "PERCENT":
      return percentOfCost(input.percent);
    case "NONE":
      return 0;
    default:
      return percentOfCost(input.supplierDefaultPercent);
  }
}

// src/shipmentReadiness.ts
function shipmentItemRequiresExpiration(input) {
  const category = (input.prepCategory ?? input.defaultPrepCategory ?? "").toLowerCase();
  return input.requiresExpirationDate === true || category.includes("expiration") || category.includes("date_restricted");
}

// src/billing.ts
function discountedBillingPrice(baseCents, discountBasisPoints) {
  if (!Number.isSafeInteger(baseCents) || baseCents < 0 || !Number.isInteger(discountBasisPoints) || discountBasisPoints < 0 || discountBasisPoints > 1e4)
    throw new Error("Invalid offer pricing");
  return Number((BigInt(baseCents) * BigInt(1e4 - discountBasisPoints) + 5000n) / 10000n);
}
var LEGACY_PLAN_LIMITS = {
  users: null,
  stores: null,
  zenoSlots: null,
  scanner: { monthly: 3e3, live: 250, bulk: 1e3 }
};
var LEGACY_LAUNCH_CATALOG = {
  version: "launch-2026-v1",
  currency: "USD",
  trialDays: 15,
  graceHours: 72,
  scanner: LEGACY_PLAN_LIMITS.scanner,
  qualifier: "ORDERS",
  tiers: [
    {
      id: "STARTER",
      name: "Starter",
      monthlyCents: 9900,
      annualCents: 100980,
      maxOrders: 500,
      maxActiveSkus: null,
      maxRevenueUsdCents: 5e5,
      limits: LEGACY_PLAN_LIMITS
    },
    {
      id: "BUSINESS",
      name: "Business",
      monthlyCents: 17900,
      annualCents: 182580,
      maxOrders: 2e3,
      maxActiveSkus: null,
      maxRevenueUsdCents: 15e5,
      limits: LEGACY_PLAN_LIMITS
    },
    {
      id: "GROWTH",
      name: "Growth",
      monthlyCents: 34900,
      annualCents: 355980,
      maxOrders: 7500,
      maxActiveSkus: null,
      maxRevenueUsdCents: 5e6,
      limits: LEGACY_PLAN_LIMITS
    },
    {
      id: "SCALE",
      name: "Scale",
      monthlyCents: 54900,
      annualCents: 559980,
      maxOrders: 2e4,
      maxActiveSkus: null,
      maxRevenueUsdCents: 15e6,
      limits: LEGACY_PLAN_LIMITS
    }
  ]
};
var LAUNCH_CATALOG = {
  version: "launch-2026-v2",
  currency: "USD",
  trialDays: 15,
  graceHours: 72,
  scanner: LEGACY_PLAN_LIMITS.scanner,
  qualifier: "ACTIVE_SKUS",
  tiers: [
    {
      id: "STARTER",
      name: "Starter",
      monthlyCents: 9900,
      annualCents: 100980,
      maxOrders: null,
      maxActiveSkus: 1e3,
      maxRevenueUsdCents: 15e5,
      limits: {
        solicitations: { monthly: 2e3 },
        maxActiveSkus: 1e3,
        maxRevenueUsdCents: 15e5,
        users: 1,
        stores: 1,
        zenoSlots: 10,
        scanner: { monthly: 1e3, live: 100, bulk: 500 }
      }
    },
    {
      id: "GROWTH",
      name: "Growth",
      monthlyCents: 34900,
      annualCents: 355980,
      maxOrders: null,
      maxActiveSkus: 1e3,
      maxRevenueUsdCents: 75e5,
      limits: {
        solicitations: { monthly: 1e4 },
        maxActiveSkus: 1e3,
        maxRevenueUsdCents: 75e5,
        users: 3,
        stores: 2,
        zenoSlots: 50,
        scanner: { monthly: 3e3, live: 250, bulk: 1e3 }
      }
    },
    {
      id: "SCALE",
      name: "Scale",
      monthlyCents: 54900,
      annualCents: 559980,
      maxOrders: null,
      maxActiveSkus: 15e3,
      maxRevenueUsdCents: 15e6,
      limits: {
        solicitations: { monthly: 25e3 },
        maxActiveSkus: 15e3,
        maxRevenueUsdCents: 15e6,
        users: 5,
        stores: 4,
        zenoSlots: 150,
        scanner: { monthly: 7500, live: 500, bulk: 2500 }
      }
    }
  ]
};
function catalogForVersion(version) {
  if (version === LAUNCH_CATALOG.version) return LAUNCH_CATALOG;
  if (version === LEGACY_LAUNCH_CATALOG.version) return LEGACY_LAUNCH_CATALOG;
  throw new Error("Unrecognized billing catalog: " + version);
}
function billingUsageIssueMessage(reasons) {
  if (reasons.some((r) => r === "MISSING_FX" || r === "INVALID_FX"))
    return "Saved Canadian revenue needs a valid USD exchange rate. Please contact support; a lower plan cannot be selected until this is resolved.";
  if (reasons.includes("UNSUPPORTED_CURRENCY"))
    return "Saved revenue includes an unsupported billing currency. Please contact support to resolve the conversion.";
  if (reasons.some((r) => r.includes("DETACHED_REVENUE")))
    return "Revenue from a removed store needs review before your plan can change. Please contact support.";
  return "Saved billing data could not be calculated. Please contact support before changing your plan.";
}

// src/referrals.ts
var REFERRAL_TIERS = [
  { name: "Starter", rate: 2e3, minimumMonthlyCents: 0, requiresReview: false },
  { name: "Silver", rate: 2500, minimumMonthlyCents: 1e5, requiresReview: false },
  { name: "Gold", rate: 3e3, minimumMonthlyCents: 15e5, requiresReview: true },
  { name: "Platinum", rate: 3500, minimumMonthlyCents: 25e5, requiresReview: true }
];
var REFERRAL_CALCULATOR_MAX_CENTS = 2e6;
function referralTier(rate) {
  const tier = REFERRAL_TIERS.find((t) => t.rate === rate);
  if (!tier) throw new Error("Invalid referral rate");
  return tier;
}
function projectedReferralRate(monthlyRevenueCents) {
  cents(monthlyRevenueCents);
  return monthlyRevenueCents >= 15e5 ? 3e3 : monthlyRevenueCents >= 1e5 ? 2500 : 2e3;
}
function referralTierProgress(monthlyRevenueCents, rate) {
  cents(monthlyRevenueCents);
  const current = referralTier(rate);
  const next = REFERRAL_TIERS.find((t) => t.rate > current.rate);
  if (!next) return null;
  return {
    nextRate: next.rate,
    nextName: next.name,
    targetCents: next.minimumMonthlyCents,
    remainingCents: Math.max(0, next.minimumMonthlyCents - monthlyRevenueCents),
    percent: Math.min(100, monthlyRevenueCents / next.minimumMonthlyCents * 100),
    requiresReview: next.requiresReview
  };
}
function referralMonthlyRevenue(collectedCents, cadence) {
  cents(collectedCents);
  return cadence === "ANNUAL" ? Math.round(collectedCents / 12) : collectedCents;
}
function referralRevenueEstimate(monthlyRevenueCents, rate) {
  const monthlyCents = referralCommission(monthlyRevenueCents, rate);
  const annualCents = monthlyCents * 12;
  if (!Number.isSafeInteger(annualCents)) throw new Error("Estimate is too large");
  return { monthlyCents, annualCents };
}
var REFERRAL_POLICY = {
  discountBasisPoints: 2e3,
  paidMonths: 3,
  standardRate: 2e3,
  growthRate: 2500,
  selectedRate: 3e3,
  eliteRate: 3500,
  holdDays: 30,
  minimumPayoutCents: 5e3,
  attributionDays: 90
};
function cents(value) {
  if (!Number.isSafeInteger(value) || value < 0) throw new Error("Invalid money amount");
}
function referralCommission(eligibleCents, rate) {
  cents(eligibleCents);
  referralTier(rate);
  return Number((BigInt(eligibleCents) * BigInt(rate) + 5000n) / 10000n);
}
function referralIntroPrice(baseCents, cadence, remainingMonths = 3) {
  cents(baseCents);
  if (!Number.isFinite(remainingMonths) || remainingMonths < 0 || remainingMonths > 3)
    throw new Error("Invalid introductory period");
  const fraction = Math.min(remainingMonths, cadence === "ANNUAL" ? 12 : 1) / (cadence === "ANNUAL" ? 12 : 1);
  return Math.round(baseCents * (1 - 0.2 * fraction));
}
function referralEstimate(input) {
  cents(input.customers);
  cents(input.monthlyCents);
  const introMonthlyCents = referralCommission(referralIntroPrice(input.monthlyCents, "MONTHLY"), input.rateBasisPoints) * input.customers;
  const ongoingMonthlyCents = referralCommission(input.monthlyCents, input.rateBasisPoints) * input.customers;
  const firstYearCents = introMonthlyCents * 3 + ongoingMonthlyCents * 9;
  if (!Number.isSafeInteger(firstYearCents)) throw new Error("Estimate is too large");
  return { introMonthlyCents, ongoingMonthlyCents, firstYearCents };
}
var REFERRAL_PAYOUT_METHODS = [
  "PAYPAL",
  "VENMO",
  "ZELLE"
];
var PAYOUT_METHOD_DETAILS = {
  PAYPAL: { name: "PayPal", handleLabel: "PayPal email" },
  VENMO: { name: "Venmo", handleLabel: "@venmo-username" },
  ZELLE: { name: "Zelle", handleLabel: "Zelle email or phone number" }
};
var TIER_QUALIFICATIONS = {
  2e3: "Free partner account",
  2500: "Unlocks automatically",
  3e3: "Customer retention review",
  3500: "Individual approval"
};
function referralProgram() {
  return {
    tiers: REFERRAL_TIERS.map((tier) => ({
      name: tier.name,
      rateBasisPoints: tier.rate,
      minimumMonthlyCents: tier.minimumMonthlyCents,
      requiresReview: tier.requiresReview,
      qualification: TIER_QUALIFICATIONS[tier.rate]
    })),
    customerDiscount: {
      basisPoints: REFERRAL_POLICY.discountBasisPoints,
      paidMonths: REFERRAL_POLICY.paidMonths,
      // Three discounted months spread over a twelve-month bill.
      annualFirstBillBasisPoints: REFERRAL_POLICY.discountBasisPoints * REFERRAL_POLICY.paidMonths / 12
    },
    payouts: {
      holdDays: REFERRAL_POLICY.holdDays,
      minimumCents: REFERRAL_POLICY.minimumPayoutCents,
      methods: REFERRAL_PAYOUT_METHODS.map((id) => ({ id, ...PAYOUT_METHOD_DETAILS[id] }))
    },
    attributionDays: REFERRAL_POLICY.attributionDays
  };
}

// src/transportationReadyDate.ts
var TRANSPORTATION_READY_HORIZON_MS = 365 * 24 * 60 * 60 * 1e3;
function dateAtOrigin(instant, timezone) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit"
  }).formatToParts(instant);
  const part = (name) => parts.find((value) => value.type === name).value;
  return `${part("year")}-${part("month")}-${part("day")}`;
}
function transportationReadyDateBounds(timezone, freight, now = /* @__PURE__ */ new Date()) {
  const today = dateAtOrigin(now, timezone);
  const min = freight ? new Date(Date.parse(`${today}T00:00:00Z`) + 864e5).toISOString().slice(0, 10) : today;
  const max = dateAtOrigin(new Date(now.getTime() + TRANSPORTATION_READY_HORIZON_MS), timezone);
  return { min, max };
}
function transportationReadyDateError(date, timezone, freight, now = /* @__PURE__ */ new Date()) {
  let bounds;
  try {
    bounds = transportationReadyDateBounds(timezone, freight, now);
  } catch {
    return "Choose a valid ship-from timezone.";
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !Number.isFinite(Date.parse(`${date}T00:00:00Z`)) || (/* @__PURE__ */ new Date(`${date}T00:00:00Z`)).toISOString().slice(0, 10) !== date) return "Choose a real calendar date.";
  if (date < bounds.min) return `Choose ${freight ? "tomorrow" : "today"} (${bounds.min}) or later in ${timezone} for the ready date.`;
  if (date > bounds.max) return `Choose a ready date on or before ${bounds.max} in ${timezone}.`;
  return null;
}

// src/amazonProducts.ts
var AMAZON_PRODUCT_CONDITIONS = [
  "NEW",
  "USED_LIKE_NEW",
  "USED_VERY_GOOD",
  "USED_GOOD",
  "USED_ACCEPTABLE",
  "REFURBISHED"
];
export {
  ALERT_THRESHOLDS,
  ALL_PERMISSIONS,
  AMAZON_PREP_CATEGORIES,
  AMAZON_PREP_TYPES,
  AMAZON_PRODUCT_CONDITIONS,
  API_ROUTES,
  AlertSeverity,
  AlertType,
  CSV_EXPORT_OPTIONS,
  ComposedCostMode,
  DASHBOARD_SCHEMA_VERSION,
  DASHBOARD_WIDGET_IDS,
  DASHBOARD_WIDGET_PERMISSION_REQUIREMENTS,
  DEFAULT_PRODUCTS_COLUMNS,
  DEFAULT_RESTOCK_COLUMNS,
  DEFAULT_RESTOCK_PARAMS,
  DEFAULT_VELOCITY_WEIGHTS,
  DEFAULT_VISIBLE_COLUMN_IDS,
  ERROR_CODES,
  EXTENDED_VELOCITY_WEIGHTS,
  FulfillmentChannel,
  HEADERS,
  INVENTORY_AUDIT_ACTIONS,
  INVENTORY_AUDIT_ACTION_LABELS,
  INVENTORY_AUDIT_SORT_FIELDS,
  InventoryLocationType,
  LAUNCH_CATALOG,
  LEGACY_LAUNCH_CATALOG,
  LEGACY_PLAN_LIMITS,
  LISTING_FLAG_COLORS,
  ListingFlag,
  ListingStatus,
  MARKETPLACE_DETAILS,
  Marketplace,
  PAGINATION,
  PERFORMANCE,
  PERMISSIONS,
  PERMISSION_DETAILS,
  PERMISSION_GROUPS,
  PREP_CATEGORY_ALIASES,
  PREP_CATEGORY_CONTRACTS,
  PREP_CATEGORY_OPTIONS,
  PREP_PHYSICAL_ACTION_LABELS,
  PRESET_ROLE_DEFINITIONS,
  PRODUCTS_COLUMN_GROUPS,
  PRODUCT_CSV_ENCODING_HEADER,
  PRODUCT_CSV_ENCODING_VERSION,
  PRODUCT_IMPORT_TEMPLATE_COLUMNS,
  PrepCategoryResolutionError,
  PrepType,
  ProductCondition,
  ProductCsvDecodingError,
  ProductStatus,
  ProductType,
  PurchaseOrderStatus,
  REFERRAL_CALCULATOR_MAX_CENTS,
  REFERRAL_PAYOUT_METHODS,
  REFERRAL_POLICY,
  REFERRAL_TIERS,
  REPRICING_COLUMN_SORTS,
  RESEARCH_ASSESSMENT_VERSION,
  RESTOCK_COLUMN_GROUPS,
  RESTOCK_FILTERS,
  RESTOCK_FILTER_CATEGORIES,
  RESTOCK_STATUS,
  RESTOCK_STATUS_COLORS,
  RESTOCK_STATUS_LABELS,
  RESTOCK_URGENCY_LEVELS,
  ROLE_SLUGS,
  RepriceStatus,
  SUPPLIER_ORDER_AMOUNT_ERROR,
  SUPPLIER_ORDER_AMOUNT_FIELDS,
  SUPPLIER_ORDER_AMOUNT_LABELS,
  SUPPLIER_ORDER_AMOUNT_MAX,
  SYNC_INTERVALS,
  ShipmentPlanStatus,
  ShipmentStatus,
  ShipmentType,
  SupplierCostBasis,
  SyncStatus,
  TRANSPORTATION_ERROR_CODES,
  TRANSPORTATION_READY_HORIZON_MS,
  VALIDATION,
  ZENO_CAROUSEL_SUGGESTIONS,
  billingUsageIssueMessage,
  buildResearchAssessment,
  calculateResearchEconomics,
  canAccessDashboardWidget,
  catalogForVersion,
  decodeImportableCsvCell,
  decodeProductCsvBytes,
  discountedBillingPrice,
  effectiveProductRebate,
  getAvailableDashboardWidgets,
  getPrepCategoryContract,
  guardrailRoiDisplay,
  inventoryAuditSortType,
  inventoryAuditSortValue,
  isCanonicalPrepCategory,
  isPermission,
  isValidSupplierOrderAmount,
  matchProductImportHeader,
  normalizePrepCategory,
  normalizePrepPhysicalAction,
  normalizeProductImportHeader,
  percentageRebate,
  physicalActionsForApiPrepTypes,
  projectedReferralRate,
  referralCommission,
  referralEstimate,
  referralIntroPrice,
  referralMonthlyRevenue,
  referralProgram,
  referralRevenueEstimate,
  referralTier,
  referralTierProgress,
  repricingColumnSortValue,
  resolveResearchPackQuantity,
  shipmentItemRequiresExpiration,
  transportationReadyDateBounds,
  transportationReadyDateError,
  validateDecodedProductCsv
};
